/**
 * Share-flow state machine (review finding L-26).
 *
 * Sharing used to be one click: POST the answer to gyro-svc `/v1/share` and silently copy the link to
 * the clipboard. A share is a link ANYONE can open without signing in (the read side of `/v1/share/:id`
 * is unauthenticated; the id is the only secret) for 7 days and cannot be revoked by the user — that
 * deserves an explicit decision and a visible result. The flow is now:
 *
 *   Share click → 'confirm' (what a link means, Create / Cancel)
 *              → 'creating' (POST in flight)
 *              → 'ready'    (link shown in a field + an explicit Copy button)  |  'failed' (retry)
 *                            |  'refused'  (the service refused: the answer holds pay data; its line is shown)
 *
 * An answer the service marked as coming from a chat that holds pay data (`classified`) shows, in place of the confirm,
 * why there is no link (GyroAssistant.tsx): no request is ever made for it.
 *
 * Pure reducer so the transitions are unit-testable; the widget renders whatever state it is in. A
 * result that arrives after the user cancelled (or for a different answer) is ignored, so a slow POST
 * can never pop a link the user did not ask for.
 */
/** Mirrors TTL_MS in services/gyro-svc/src/shares.ts (7 days) — surfaced in the confirm text. */
export const SHARE_TTL_DAYS = 7;
/** Why an answer from a chat that holds pay data is never shared by link (gyro-svc PAY_SHARE_REFUSED_TEXT, the same words). */
export const PAY_DATA_NOT_SHAREABLE_TEXT = "This answer comes from a chat that holds pay data, so it can't be shared by link. Copy it or export it instead.";
export const SHARE_CONFIRM_TEXT = `Share this answer? Anyone with the link can open it — no sign-in needed — for ${SHARE_TTL_DAYS} days, and it cannot be revoked early.`;
export function shareReducer(state, ev) {
    switch (ev.type) {
        case 'request':
            // Clicking Share again on the answer whose confirm is already open toggles it closed; any other
            // click (another answer, a finished/failed flow) re-opens the confirm step for that answer.
            if (state && state.key === ev.key && state.step === 'confirm')
                return null;
            return { step: 'confirm', key: ev.key };
        case 'cancel':
            return null;
        case 'confirm':
            // A link is minted ONLY from an explicit confirm on an open confirm step — never from a bare click.
            if (!state || state.step !== 'confirm')
                return state;
            return { step: 'creating', key: state.key };
        case 'created':
            // Stale result (user cancelled, or a different answer is now in flight): ignore it.
            if (!state || state.step !== 'creating' || state.key !== ev.key)
                return state;
            return ev.url ? { step: 'ready', key: state.key, url: ev.url, copy: 'idle' } : { step: 'failed', key: state.key };
        case 'copied':
            if (!state || state.step !== 'ready')
                return state;
            return { ...state, copy: 'copied' };
        case 'copy-failed':
            if (!state || state.step !== 'ready')
                return state;
            return { ...state, copy: 'failed' };
        case 'refused':
            // Like a result: only for the request still in flight for that answer.
            if (!state || state.step !== 'creating' || state.key !== ev.key)
                return state;
            return { step: 'refused', key: state.key, message: ev.message };
        default:
            return state;
    }
}
//# sourceMappingURL=share.js.map